import React from "react";

import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";

import { displayEdit, buildEmptyObject } from "@auto/widgets/EditComponent";

import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";

import styles from "./UserAccountForm.module.styl";

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value.replace("T", " ").slice(0, 19);
  }

  try {
    return new Date(value)
      .toISOString()
      .replace("T", " ")
      .slice(0, 19);
  } catch {
    return String(value);
  }
};

const getField = (row, camelName, snakeName) => {
  return row?.[camelName] ?? row?.[snakeName] ?? "";
};

const fullName = (account) => {
  const firstName = getField(
    account,
    "firstName",
    "first_name"
  );

  const lastName = getField(
    account,
    "lastName",
    "last_name"
  );

  return `${firstName} ${lastName}`.trim();
};

const normalizeStatus = (value) => {
  return String(value || "")
    .trim()
    .toUpperCase();
};

const statusLabel = (value) => {
  const status = normalizeStatus(value);

  if (!status) {
    return "";
  }

  const statusLabels = {
    ACTIVE: "common.active",
    DISABLED: "common.disabled",
    PENDING: "common.pending",
    APPROVED: "common.approved",
    REJECTED: "common.rejected",
    ERROR: "common.error"
  };

  return statusLabels[status]
    ? t(statusLabels[status])
    : status
        .toLowerCase()
        .replaceAll("_", " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

const readByGid = async (entity, gid) => {
  if (!gid) {
    return null;
  }

  return await rest.request(
    getServiceUri() + `${entity}/${gid}`,
    "GET"
  );
};

const readUserAccountListRecord = async (gid) => {
  if (!gid) {
    return null;
  }

  const rows = await rest.request(
    getServiceUri() + "user-account-list/search",
    "POST",
    {
      gid: Number(gid),
      search: "",
      filters: {},
      limit: 1,
      offset: 0
    }
  );

  return rows?.[0] || null;
};

const loadUserAccountReadData = async (gid, gui) => {
  const account =
    await readUserAccountListRecord(gid);

  if (!account) {
    return {};
  }

  const approvalRequestGid = getField(
    account,
    "approvalRequestGid",
    "approval_request_gid"
  );

  const approvalRequest =
    approvalRequestGid && can(gui, "READ_USER_ACCOUNT_REQUEST")
      ? await readByGid(
          "user-account-request",
          approvalRequestGid
        )
      : null;

  return {
    ...account,

    approvalRequestGid,

    personGid:
      getField(
        account,
        "personGid",
        "person_gid"
      ) || null,

    defaultImageId:
      getField(
        account,
        "defaultImageId",
        "default_image_id"
      ) || null,

    votingCenterId:
      getField(
        account,
        "votingCenterId",
        "voting_center_id"
      ) || null,

    votingCenterLabel:
      getField(
        account,
        "votingCenterLabel",
        "voting_center_label"
      ),

    fullName:
      fullName(account),

    statusLabel:
      statusLabel(
        getField(
          account,
          "status",
          "status"
        )
      ),

    createdAtFormatted:
      formatDateTime(
        getField(
          account,
          "createdAt",
          "created_at"
        )
      ),

    updatedAtFormatted:
      formatDateTime(
        getField(
          account,
          "updatedAt",
          "updated_at"
        )
      ),

    requestStatus:
      getField(
        approvalRequest,
        "requestStatus",
        "request_status"
      ),

    requestSubmittedAt:
      formatDateTime(
        getField(
          approvalRequest,
          "submittedAt",
          "submitted_at"
        )
      )
  };
};

const personPhotoSrc = (data) => {
  if (!data?.defaultImageId) {
    return "";
  }

  return (
    getServiceUri() +
    `person-photo/data/${Number(data.defaultImageId)}`
  );
};

const SectionCard = ({
  icon,
  title,
  subtitle,
  children
}) => {
  const Icon = icon;

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div className={styles.cardTitleRow}>
          <div className={styles.cardIconWrap}>
            <Icon className={styles.cardIcon} />
          </div>

          <div className={styles.cardHeaderText}>
            <h2 className={styles.cardTitle}>
              {title}
            </h2>

            {subtitle ? (
              <div className={styles.cardSubtitle}>
                {subtitle}
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <div className={styles.cardBody}>
        {children}
      </div>
    </section>
  );
};

const Field = ({ label, value }) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  return (
    <div className={styles.field}>
      <div className={styles.fieldLabel}>
        {label}
      </div>

      <div className={styles.fieldValue}>
        {String(value)}
      </div>
    </div>
  );
};

const LinkedField = ({
  label,
  value,
  href,
  gui
}) => {
  if (!value || !href) {
    return null;
  }

  return (
    <div className={styles.field}>
      <div className={styles.fieldLabel}>
        {label}
      </div>

      <button
        type="button"
        className={styles.linkValue}
        onClick={() => gui.goTo(href)}
      >
        {String(value)}
      </button>
    </div>
  );
};

const PersonPicture = ({
  data,
  gui
}) => {
  const src = can(gui, "READ_PERSON_PHOTO") ? personPhotoSrc(data) : "";

  const content = src ? (
    <img
      src={src}
      alt={data.fullName || t("common.person")}
      style={{
        display: "block",
        width: 72,
        height: 72,
        borderRadius: "50%",
        objectFit: "cover",
        objectPosition: "center",
        background: "#eef2f7"
      }}
    />
  ) : (
    <AccountCircleOutlinedIcon
      sx={{
        width: 72,
        height: 72,
        color: "rgba(15, 23, 42, 0.28)"
      }}
    />
  );

  if (!data.personGid || !can(gui, "READ_PERSON")) {
    return content;
  }

  return (
    <button
      type="button"
      title={t("user.openPersonRecord")}
      aria-label={t("user.openPersonRecord")}
      onClick={() =>
        gui.goTo(
          `/person/${data.personGid}`
        )
      }
      style={{
        display: "block",
        padding: 0,
        margin: 0,
        border: 0,
        borderRadius: "50%",
        background: "transparent",
        cursor: "pointer"
      }}
    >
      {content}
    </button>
  );
};

const UserAccountHeader = ({
  data,
  gui
}) => {
  return (
    <section className={styles.header}>
      <div className={styles.headerMain}>
        <PersonPicture
          data={data}
          gui={gui}
        />

        <div className={styles.headerText}>
          <h1 className={styles.headerTitle}>
            {data.fullName ||
              data.username ||
              t("user.userAccount")}
          </h1>

          <div className={styles.headerSubtitle}>
            {data.username
              ? `${t("user.username")}: ${data.username}`
              : t("user.fieldOperatorAccount")}
          </div>
        </div>
      </div>

      {data.statusLabel ? (
        <div className={styles.statusPill}>
          {data.statusLabel}
        </div>
      ) : null}
    </section>
  );
};

const UserAccountReadView = ({
  gui,
  id
}) => {
  const [data, setData] =
    React.useState(null);

  const [error, setError] =
    React.useState("");

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setError("");

        const account =
          await loadUserAccountReadData(id, gui);

        if (!cancelled) {
          setData(account);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.message || String(err)
          );

          setData(null);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [gui, id]);

  if (error) {
    return (
      <div className={styles.error}>
        {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.loading}>
        {t("common.loading")}
      </div>
    );
  }

  const nationalId =
    getField(
      data,
      "nationalId",
      "national_id"
    );

  return (
    <div className={styles.page}>
      <UserAccountHeader
        data={data}
        gui={gui}
      />

      <div className={styles.gridTwo}>
        <SectionCard
          icon={AccountCircleOutlinedIcon}
          title={t("person.identity")}
          subtitle={t("user.identitySubtitle")}
        >
          <div className={styles.fieldsGrid}>
            <Field
              label={t("person.firstName")}
              value={getField(
                data,
                "firstName",
                "first_name"
              )}
            />

            <Field
              label={t("person.lastName")}
              value={getField(
                data,
                "lastName",
                "last_name"
              )}
            />

            <Field
              label={t("user.username")}
              value={data.username}
            />

            {data.personGid && can(gui, "READ_PERSON") ? (
              <LinkedField
                gui={gui}
                label={t("person.nationalId")}
                value={nationalId}
                href={`/person/${data.personGid}`}
              />
            ) : (
              <Field
                label={t("person.nationalId")}
                value={nationalId}
              />
            )}
          </div>
        </SectionCard>

        <SectionCard
          icon={PlaceOutlinedIcon}
          title={t("voter.votingCenter")}
          subtitle={t("user.votingCenterSubtitle")}
        >
          <div className={styles.fieldsGrid}>
            <Field
              label={t("voter.votingCenter")}
              value={data.votingCenterLabel}
            />
          </div>
        </SectionCard>
      </div>

      <div className={styles.gridTwo}>
        <SectionCard
          icon={BadgeOutlinedIcon}
          title={t("user.account")}
          subtitle={t("user.currentAccountStatus")}
        >
          <div className={styles.fieldsGrid}>
            <Field
              label={t("common.status")}
              value={data.statusLabel}
            />

            <Field
              label={t("user.createdAt")}
              value={data.createdAtFormatted}
            />

            <Field
              label={t("user.updatedAt")}
              value={data.updatedAtFormatted}
            />
          </div>
        </SectionCard>

        <SectionCard
          icon={VpnKeyOutlinedIcon}
          title={t("user.approvalRequest")}
          subtitle={t("user.approvalRequestSubtitle")}
        >
          <div className={styles.fieldsGrid}>
            {data.approvalRequestGid && can(gui, "READ_USER_ACCOUNT_REQUEST") ? (
              <LinkedField
                gui={gui}
                label={t("user.approvalRequest")}
                value={t("user.openRequest")}
                href={`/user-account-request/${data.approvalRequestGid}`}
              />
            ) : (
              <Field
                label={t("user.approvalRequest")}
                value={data.approvalRequestGid}
              />
            )}

            <Field
              label={t("user.requestStatus")}
              value={data.requestStatus}
            />

            <Field
              label={t("voter.submittedAt")}
              value={data.requestSubmittedAt}
            />
          </div>
        </SectionCard>
      </div>

      <SectionCard
        icon={ScheduleOutlinedIcon}
        title={t("user.audit")}
        subtitle={t("user.lifecycleTimestamps")}
      >
        <div className={styles.fieldsGrid}>
          <Field
            label={t("user.createdAt")}
            value={data.createdAtFormatted}
          />

          <Field
            label={t("user.updatedAt")}
            value={data.updatedAtFormatted}
          />
        </div>
      </SectionCard>
    </div>
  );
};

export const userAccountProjection = {
  sections: [
    {
      title: "person.identity",

      fields: [
        {
          name: "firstName",
          label: "person.firstName",
          valueType: "text",
          inputType: "text",
          span: 6
        },

        {
          name: "lastName",
          label: "person.lastName",
          valueType: "text",
          inputType: "text",
          span: 6
        },

        {
          name: "username",
          label: "user.username",
          valueType: "text",
          inputType: "text",
          span: 6
        },

        {
          name: "nationalId",
          label: "person.nationalId",
          valueType: "text",
          inputType: "text",
          span: 6
        }
      ]
    },

    {
      title: "user.account",

      fields: [
        {
          name: "status",
          label: "common.status",
          valueType: "text",
          inputType: "text",
          span: 6
        },

        {
          name: "votingCenterId",
          label: "voter.votingCenterId",
          valueType: "number",
          inputType: "number",
          span: 6
        },

        {
          name: "approvalRequestGid",
          label: "user.approvalRequestGid",
          valueType: "number",
          inputType: "number",
          span: 6
        },

        {
          name: "createdAt",
          label: "user.createdAt",
          valueType: "datetime",
          inputType: "datetime-local",
          span: 6
        },

        {
          name: "updatedAt",
          label: "user.updatedAt",
          valueType: "datetime",
          inputType: "datetime-local",
          span: 6
        }
      ]
    }
  ]
};

export const displayReadUserAccount = (
  gui,
  id
) => {
  return {
    uuid: `user-account-read-${id}`,

    view: () => (
      <UserAccountReadView
        gui={gui}
        id={id}
      />
    )
  };
};

export const displayEditUserAccount = (
  gui,
  id,
  onFinish
) => {
  return displayEdit({
    gui,

    projection:
      userAccountProjection,

    loadData: async () => {
      if (!id) {
        return buildEmptyObject(
          userAccountProjection
        );
      }

      return await readByGid(
        "user-account",
        id
      );
    },

    onSubmit: async (values) => {
      const response =
        await rest.request(
          getServiceUri() +
            (
              id
                ? "user-account/update-document"
                : "user-account/new-document"
            ),
          "POST",
          values
        );

      if (onFinish) {
        onFinish(response);
      }

      return response;
    }
  });
};